import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import "../styles/adminchat.css";

// Socket.IO connection URL – domain only (connects to default namespace /)
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "https://on-excapp.app";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || `${window.location.origin}/api`;

// For debugging – you can remove these after confirming it works
console.log("Socket.IO connecting to:", SOCKET_URL);
console.log("API base URL:", API_BASE_URL);

const TRADERS_URL = `${API_BASE_URL}/chats/senders`;

function AdminChat() {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [user, setUser] = useState(null);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const fileInputRef = useRef(null);
  const [traders, setTraders] = useState([]);
  const [isTraderListOpen, setIsTraderListOpen] = useState(false);
  const [isLoadingTraders, setIsLoadingTraders] = useState(false);
  const [traderFetchError, setTraderFetchError] = useState(null);

  // Load user and token
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("access_token");
    if (storedUser && storedToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser({ ...parsedUser, access_token: storedToken });
      } catch (e) {
        console.error("Failed parsing user:", e);
      }
    }
  }, []);

  // Normalize message
  const normalizeMessage = (msg) => ({
    id: msg.id,
    tempId: msg.tempId || null,
    content: msg.content,
    senderId: msg.sender?.id || msg.senderId,
    receiverId: msg.receiver?.id || msg.receiverId,
    senderType: msg.sender?.role === "admin" ? "admin" : "trader",
    senderName: msg.sender?.username || "Unknown",
    createdAt: msg.created_at || msg.createdAt || new Date().toISOString(),
    isFile: !!msg.isFile,
    fileName: msg.fileName || null,
    fileType: msg.fileType || null,
  });

  // Send text or file message
  const sendMessage = (msg, isFile = false) => {
    if (!msg.content?.trim() && !isFile) return;
    if (!user || (user.role === "admin" && !selectedUser)) return;

    const tempId = `temp-${Date.now()}`;
    const msgToSend = {
      ...msg,
      senderId: user.id,
      receiverId: user.role === "admin" ? selectedUser.id : "admin",
      senderType: user.role,
      isRead: false,
      isFile,
      tempId,
    };

    // Optimistic UI update
    setMessages((prev) => [
      ...prev,
      normalizeMessage({
        ...msgToSend,
        sender: { id: user.id, role: user.role, username: user.name || "You" },
      }),
    ]);

    // Emit via socket
    socketRef.current?.emit("sendMessage", msgToSend);

    // Save to backend via REST
    fetch(`${API_BASE_URL}/chats/send`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user.access_token}`,
      },
      body: JSON.stringify(msgToSend),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to save message");
        return res.json();
      })
      .then((data) => {
        // Replace optimistic message with real one
        setMessages((prev) =>
          prev.map((m) => (m.tempId === tempId ? normalizeMessage(data) : m)),
        );
      })
      .catch((err) => console.error("Send message error:", err));
  };

  // Handle file upload
  const handleSendFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      sendMessage(
        {
          content: reader.result,
          fileName: file.name,
          fileType: file.type,
        },
        true,
      );
    };
    reader.readAsDataURL(file);
  };

  const markMessagesAsRead = (senderId) => {
    socketRef.current?.emit("markAsRead", {
      senderId,
      adminId: user.id,
    });
    if (user.role === "admin") {
      setTraders((prev) =>
        prev.map((t) => (t.id === senderId ? { ...t, unread: 0 } : t)),
      );
    }
  };

  // Socket connection
  useEffect(() => {
    if (!user) return;

    if (!SOCKET_URL) {
      throw new Error("REACT_APP_SOCKET_URL is not defined");
    }

    socketRef.current = io(SOCKET_URL, {
      path: "/socket.io",
      auth: { token: user.access_token },
      transports: ["websocket"],
    });
    socketRef.current.on("connect", async () => {
      setIsConnected(true);
      console.log("Socket connected!");

      if (user.role === "admin") {
        socketRef.current.emit("join", { userId: user.id, role: "admin" });
        socketRef.current.emit("adminOnline", { adminId: user.id });

        setIsLoadingTraders(true);
        fetch(TRADERS_URL, {
          headers: { Authorization: `Bearer ${user.access_token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            setTraders(
              data.map((t) => ({
                id: t.sender?.id,
                name: t.sender?.username || "Trader",
                online: !!t.sender?.online,
                unread: t.unreadCount || 0,
              })),
            );
            setTraderFetchError(null);
          })
          .catch(() => setTraderFetchError("Failed to load traders."))
          .finally(() => setIsLoadingTraders(false));
      } else if (user.role === "trader") {
        socketRef.current.emit("join", { userId: user.id, role: "trader" });
        try {
          const res = await fetch(
            `${API_BASE_URL}/chats/messages/sender/${user.id}`,
            { headers: { Authorization: `Bearer ${user.access_token}` } },
          );
          const data = await res.json();
          if (data?.messages) setMessages(data.messages.map(normalizeMessage));
        } catch (err) {
          console.error("Error loading trader messages:", err);
        }
      }
    });

    socketRef.current.on("receiveMessage", (msg) => {
      const normalized = normalizeMessage(msg);

      setMessages((prev) => {
        if (
          prev.some(
            (m) => m.id === normalized.id || m.tempId === normalized.tempId,
          )
        ) {
          return prev;
        }
        return [...prev, normalized];
      });

      if (user.role === "admin" && selectedUser) {
        if (
          normalized.senderId === selectedUser.id ||
          normalized.receiverId === selectedUser.id
        ) {
          markMessagesAsRead(normalized.senderId);
        } else {
          setTraders((prev) =>
            prev.map((t) =>
              t.id === normalized.senderId ? { ...t, unread: t.unread + 1 } : t,
            ),
          );
        }
      }
    });

    socketRef.current.on("disconnect", () => {
      setIsConnected(false);
      console.log("Socket disconnected");
    });

    return () => {
      if (user?.role === "admin") {
        socketRef.current?.emit("adminOffline", { adminId: user.id });
      }
      socketRef.current?.disconnect();
    };
  }, [user, selectedUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    sendMessage({ content: newMessage });
    setNewMessage("");
  };

  const handleSelectTrader = async (trader) => {
    setSelectedUser(trader);
    setMessages([]);
    markMessagesAsRead(trader.id);

    try {
      const res = await fetch(
        `${API_BASE_URL}/chats/messages/sender/${trader.id}`,
        { headers: { Authorization: `Bearer ${user.access_token}` } },
      );
      const data = await res.json();
      const filtered = data.messages.filter(
        (m) => m.sender?.id === trader.id || m.receiver?.id === trader.id,
      );
      setMessages(filtered.map(normalizeMessage));

      socketRef.current?.emit("requestConversation", {
        adminId: user.id,
        clientId: trader.id,
      });
    } catch (err) {
      console.error("Error loading conversation:", err);
    }

    setIsTraderListOpen(false);
  };

  const toggleTraderList = () => setIsTraderListOpen(!isTraderListOpen);

  return (
    <div
      className={`adminchat-page ${
        user?.role === "trader" ? "trader-user" : ""
      }`}
    >
      <div className="adminchat-container">
        <div className="adminchat-header">
          <div className="header-left">
            {user?.role === "admin" && (
              <button className="trader-toggle" onClick={toggleTraderList}>
                ☰
              </button>
            )}
            <h2>{user?.role === "admin" ? "Admin Chat" : "Chat with Admin"}</h2>
          </div>
          <div className="connection-status">
            <span
              className={`status-indicator ${isConnected ? "online" : "offline"}`}
            />
            <span>{isConnected ? "Online" : "Offline"}</span>
          </div>
        </div>

        <div className="adminchat-content">
          {user?.role === "admin" && (
            <div className={`trader-list ${isTraderListOpen ? "active" : ""}`}>
              <h4>Traders</h4>
              {isLoadingTraders && <p>Loading traders...</p>}
              {traderFetchError && <p className="error">{traderFetchError}</p>}
              {!isLoadingTraders &&
                traders.map((t) => (
                  <div
                    key={t.id}
                    className={`trader-item ${
                      selectedUser?.id === t.id ? "selected" : ""
                    }`}
                    onClick={() => handleSelectTrader(t)}
                  >
                    <div>
                      <strong>{t.name}</strong>
                      <div
                        className={
                          t.online ? "status-online" : "status-offline"
                        }
                      >
                        {t.online ? "Online" : "Offline"}
                      </div>
                    </div>
                    {t.unread > 0 && (
                      <span className="unread-badge">{t.unread}</span>
                    )}
                  </div>
                ))}
            </div>
          )}

          <div className="message-panel">
            <div className="wa-adminchat-messages">
              {user?.role === "admin" && !selectedUser ? (
                <div className="wa-empty-adminchat">
                  Select a trader to start chatting.
                </div>
              ) : messages.length === 0 ? (
                <div className="wa-empty-adminchat">No messages yet.</div>
              ) : (
                messages.map((msg, i) => (
                  <div
                    key={msg.id || msg.tempId || i}
                    className={`wa-msg ${
                      msg.senderType === "trader" ? "incoming" : "outgoing"
                    }`}
                  >
                    <div className="wa-msg-bubble">
                      {msg.isFile ? (
                        msg.fileType?.startsWith("image/") ? (
                          <img
                            src={msg.content}
                            alt={msg.fileName}
                            className="chat-image"
                            style={{
                              maxWidth: "120px",
                              maxHeight: "120px",
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                          />
                        ) : (
                          <div className="file">
                            <div className="fname">{msg.fileName}</div>
                            <a href={msg.content} download={msg.fileName}>
                              Download File
                            </a>
                          </div>
                        )
                      ) : (
                        <div className="wa-msg-text">{msg.content}</div>
                      )}
                      <div className="wa-msg-time">
                        {new Date(msg.createdAt).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="wa-input">
              <input
                type="text"
                placeholder="Message"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={user?.role === "admin" && !selectedUser}
              />
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/*"
                onChange={(e) => handleSendFile(e.target.files[0])}
              />
              <button
                type="button"
                className="attachment-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={user?.role === "admin" && !selectedUser}
              >
                📎
              </button>
              <button
                className="wa-send"
                onClick={handleSendMessage}
                disabled={user?.role === "admin" && !selectedUser}
              >
                ➤
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminChat;
